import React from "react";
import { SNACKBAR_VARIANTS } from "../../../../common/constants";
import searchFormUtils from "../../details/form/utils/searchFormUtils";
import { BadgePaymentStatus, PaymentMethods } from "../../../../common/enums";
import { useDispatch, useSelector } from "react-redux";
import { badgeOrdersSelector, badgesSelector } from "../../../../redux/eventEdition/selectors/eventEdition.selectors";
import { IPersonBadge } from "../../../../common/types";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import moment from "moment";
import { BadgeOrder } from "../../../../redux/types";
import useReceiptPrint from "../../../../common/hooks/useReceiptPrint/useReceiptPrint";
import cloneDeep from "lodash.clonedeep";
import PublicApiService from "../../../../services/PublicApiService";
import { UpgradeCompleteOptions } from "@reedexpo/mercury-badge-manager-components";
import { setEventEditionBadges } from "../../../../redux/eventEdition/slice/eventEditionSlice";

type Props = {
  updateSelectedBadges?: () => void;
  showError?: (err: string) => void;
  selectedBadges: IPersonBadge[];
  deselectRows?: () => void;
  setUpgradeDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useUnpaidBadgePayment = ({ selectedBadges, updateSelectedBadges, showError, deselectRows, setUpgradeDialogOpen }: Props) => {
  const dispatch = useDispatch();
  const badges = useSelector(badgesSelector);
  const { t } = useTranslation("common");
  const { enqueueSnackbar } = useSnackbar();
  const orders: BadgeOrder[] = useSelector(badgeOrdersSelector);
  const { printReceipt, persistReceipt } = useReceiptPrint();

  const getOrdersByBadgeId = (badgeId: string) => {
    return orders.filter(order => order.badgeId === badgeId);
  };

  const getUnpaidOrdersForBadges = (badges: IPersonBadge[]) => {
    const orders: BadgeOrder[] = [];
    badges.forEach(badge => {
      const order = getUnpaidBadgeOrder(badge);
      if (order[0]) {
        orders.push(order[0]);
      }
    });
    return orders;
  };

  const handleUnpaidBadgePayment = async (
    _newCustomerType: string,
    _newBadgeProduct: string | undefined,
    price: { priceWithTax: number; priceWithoutTax: number },
    orderId: string,
    shouldPrintReceipt?: boolean,
    paymentMethod?: PaymentMethods,
    authorisationCode?: string,
    referenceNumber?: string,
    options?: UpgradeCompleteOptions
  ) => {
    const selectedBadge = selectedBadges[0];

    if (!selectedBadge) {
      enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
      showError?.(t("errors.clientError"));
      return;
    }

    try {
      await PublicApiService.postPartialPayments(options!.partialPaymentsData!);

      const receiptPayload = {
        newBadgeProduct: _newBadgeProduct,
        badge: selectedBadge,
        price: price.priceWithTax,
        authorisationCode,
        paymentMethod,
        orderId,
        referenceNumber,
        options,
        badgeId: selectedBadge.id,
        orders: getUnpaidOrdersForBadges(selectedBadges)
      };

      if (shouldPrintReceipt) {
        await printReceipt(receiptPayload);
      } else {
        await persistReceipt(receiptPayload);
      }

      let updatedBadges = cloneDeep(badges);
      selectedBadges.forEach(badge => {
        updatedBadges = searchFormUtils.getBadgesWithReplacedItem(badges, {
          ...badge,
          paymentStatus: BadgePaymentStatus.PAID
        });
      });

      dispatch(setEventEditionBadges(updatedBadges));
      enqueueSnackbar(<b>{t("search.badgeDetails.paymentCompletedSuccessfully")}</b>, SNACKBAR_VARIANTS.success);
      updateSelectedBadges?.();
      deselectRows?.();
      if (setUpgradeDialogOpen) {
        setUpgradeDialogOpen(false);
      }
    } catch (e) {
      if ((e as Error).message !== "Network error") {
        enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
        showError?.(t("errors.clientError"));
        return;
      }
    }
  };

  const handlePaymentOverride = () => {
    let updatedBadges = cloneDeep(badges);
    selectedBadges.forEach(badge => {
      updatedBadges = searchFormUtils.getBadgesWithReplacedItem(badges, {
        ...badge,
        paymentStatus: BadgePaymentStatus.PAID
      });
    });
    dispatch(setEventEditionBadges(updatedBadges));
    updateSelectedBadges?.();
    if (setUpgradeDialogOpen) {
      setUpgradeDialogOpen(false);
    }
  };

  const getUnpaidBadgeOrder = (providedBadge?: IPersonBadge) => {
    const badgeOrders = getOrdersByBadgeId(providedBadge?.id || badges[0]!.id);
    const latestOrder = badgeOrders.reduce((acc, order) => {
      //TODO use dayjs instead of moment
      if (order.paymentStatus === BadgePaymentStatus.UNPAID && moment(order.createdDate).isAfter(moment(acc?.createdDate))) {
        return order;
      }
      return acc;
    }, badgeOrders[0]);
    return latestOrder ? [latestOrder] : [];
  };

  return {
    handleUnpaidBadgePayment,
    getUnpaidBadgeOrder,
    handlePaymentOverride
  };
};
